import {IHttpResponse} from './Service';
import {AuthenticatedService} from './AuthenticatedService';
import {Page} from '../Models';
import {Author} from './AuthorService';

export interface Article {
  id: number;
  title: string;
  body: string;
  date: Date;
  author: Author;
  published: boolean;
  categories: string[];
  image: number;
  upvotes: number;
  downvotes: number;
}

export class ArticleService extends AuthenticatedService {
  public list = async (page: number = 1, modules: string[]): Promise<IHttpResponse<Page<Article>>> => {
    const moduleArg = modules.map(m => `modules=${m}`).join('&');

    const articles = await this.getAuth<Page<Article>>(
      `articles/?${moduleArg}&page=${page}`,
    );

    return articles;
  }

  public listAll = async (page: number = 1): Promise<IHttpResponse<Page<Article>>> => {
    const articles = await this.getAuth<Page<Article>>(
      `articles/all?page=${page}`,
    );

    return articles;
  }

  public listByAuthor = async (author: number, page: number = 1): Promise<IHttpResponse<Page<Article>>> => {
    const articles = await this.getAuth<Page<Article>>(
      `articles/author/${author}?page=${page}`,
    );

    return articles;
  }

  public listByCategory = async (category: string, page: number = 1): Promise<IHttpResponse<Page<Article>>> => {
    const articles = await this.getAuth<Page<Article>>(
      `articles/category/${category}?page=${page}`,
    );

    return articles;
  }

  public byId = async (id: number): Promise<IHttpResponse<Article>> => {
    const article = await this.getAuth<Article>(
      `articles/${id}`,
    );

    return article;
  }

  public search = async (terms: string, page: number = 1): Promise<IHttpResponse<Page<Article>>> => {
    terms = encodeURIComponent(terms);
    const articles = await this.getAuth<Page<Article>>(
      `articles/search/?terms=${terms}&page=${page}`,
    );

    return articles;
  }

  public create = async (article: Article): Promise<IHttpResponse<Article>> => {
    const newArticle = await this.postAuth<Article>(
      'articles', article,
    );

    return newArticle;
  }

  public update = async (article: Article): Promise<IHttpResponse<Article>> => {
    const newArticle = await this.putAuth<Article>(
      `articles/${article.id}`, article,
    );

    return newArticle;
  }

  public remove = async (id: number): Promise<IHttpResponse<Article>> => {
    const newArticle = await this.deleteAuth<Article>(
      `articles/${id}`,
    );

    return newArticle;
  }

  public getVote = async (id: number): Promise<IHttpResponse<boolean>> => {
    const vote = await this.getAuth<boolean>(
      `articles/${id}/vote`,
    );

    return vote;
  }

  public vote = async (id: number, upvote?: boolean): Promise<IHttpResponse<Article>> => {
    if (upvote === undefined) {
      return await this.deleteAuth<Article>(
        `articles/${id}/vote`,
      );
    } else {
      return await this.postAuth<Article>(
        `articles/${id}/vote`, { upvote },
      );
    }
  }

  public image = (id: number): string => {
    return `${this.config.BaseUrl}/articles/image/${id}`;
  }
}
